/**
 * Прибавить или вычесть от нужной даты
 * ====================================
 * Types of routes:
 * - Exact date:
 *     - 3000-days-before-29-january-2023
 *
 * - Fixed urls for current year:
 *    - 3000-days-before-january-15
 *    - 6-months-from-may-1
 *    - 6-months-from-may-1
 *
 * - Dynamic dates (this kind of URLs will always be present in search and get higher page weight):
 *    - 3000-days-after-today
 *    - 3000-days-after-tomorrow
 *    - 3000-days-before-today
 *    - 3000-weeks-from-tomorrow
 *
 * TODO Добавить контент на страницы в зависимости от количества дней после сегодня
 *  - по смерти: 9, 40
 * TODO Можно взять факты о дне из википедии
 *    Однако чтобы не увеличивать время загрузки, надо пропарсить википедию хотя бы на год вперед и назад и закэшировать
 *    К тому же там много фактов, как отфильтровать лишнее?
 * TODO Факты о годе: Международные годы ООН:
 *    https://ru.wikipedia.org/wiki/%D0%9C%D0%B5%D0%B6%D0%B4%D1%83%D0%BD%D0%B0%D1%80%D0%BE%D0%B4%D0%BD%D1%8B%D0%B9_%D0%B3%D0%BE%D0%B4_%D0%9E%D0%9E%D0%9D
 * TODO Индексируются ли в итоге SVG картинки?
 * TODO Дату в пикере красиво форматируй
 * TODO If you want to count only Business Days (Source: https://calculat.io/en/date/count/30--days--from--today)
 *    Достаточно просто ссылки на страницу, чтобы дважды не считать и два раза показать рекламу
 * TODO Копирование даты в локалях как тут: https://www.thecalculatorsite.com/time/days/30
 * TODO Add working days for other countries
 * TODO Кнопка Добавить в календарь
 */
import { LoaderFunctionArgs, MetaFunction, redirect } from "react-router";
import { Link, useLoaderData, useLocation } from "react-router";
import { TFunction } from "i18next";
import React, { FormEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import { addNoindexMeta, ShouldIndexMeta } from "~/helpers/meta-noindex";
import { getBaseUrl } from "~/helpers/getBaseUrl";
import ArticleImage from "~/components/ArticleImage";
import Button from "~/components/Button";
import Code from "~/components/Code";
import CodeBlock from "~/components/CodeBlock";
import NumberInput from "~/components/forms/NumberInput";
import SelectInput from "~/components/forms/SelectInput";
import H2 from "~/components/H2";
import H3 from "~/components/H3";
import Icon from "~/components/Icon";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Author from "~/components/tool-page/Author";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import ToolHeader from "~/components/tool-page/ToolHeader";
import { DateDiff, dateInfo, getChineseZodiac, getZodiacSign } from "~/helpers/dates.server";
import { getDatesDiff } from "~/helpers/dates";
import { formatLongDate } from "~/helpers/dates";
import {
  calculateResultingDate,
  createDescriptionFromQuery,
  createTitleFromQuery,
  EdgeCaseRedirectError,
  InvalidDateError,
  InvalidQueryError,
  parseSlug,
  Query
} from "~/helpers/dates/count.server";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";

import { workSchedule } from "../../../../data/holidays-russia.server";
import DatePicker from "../../../components/forms/DatePicker";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import BetterNumberInput from "~/components/forms/BetterNumberInput";

const CATEGORY = "dates";
const TKEY = "calculators.dates.count";
const ROUTE_ENDING = "count";
const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");


export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      data?.meta.lastModified ? new Date(data.meta.lastModified) : null,
      schemaOrg.authorAlexFedorov
    ),
    // Open graph
    { property: "og:title", content: makeTitle(data?.meta.title) },
    { property: "og:type", content: "website" },
    { property: "og:url", content: data?.meta.currentUrl },
    { property: "og:image", content: data?.meta.images[0] ?? "" },
    { property: "og:description", content: data?.meta.description },
    { property: "og:locale", content: data?.meta.locale }
  ];

  addNoindexMeta(meta, data.meta.shouldIndex);

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  return meta;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"],
  i18n: ["translations", "dates"]
};

function createSlug(number: number, unit: string, direction: string, date: Date): string {
  const today = new Date();

  // Handle today dynamic date
  if (
    today.getFullYear() === date.getFullYear()
    && today.getMonth() === date.getMonth()
    && today.getDate() === date.getDate()
  ) {
    return `${number}-${unit}-${direction}-today`.toLowerCase();
  }

  // Handle tomorrow dynamic date
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (
    tomorrow.getFullYear() === date.getFullYear()
    && tomorrow.getMonth() === date.getMonth()
    && tomorrow.getDate() === date.getDate()
  ) {
    return `${number}-${unit}-${direction}-tomorrow`.toLowerCase();
  }

  // Handle specific date case
  const monthName = date.toLocaleString("en", { month: "long" });
  const year = date.getFullYear();

  // If year is current year, omit it in slug
  if (year === new Date().getFullYear()) {
    return `${number}-${unit}-${direction}-${date.getDate()}-${monthName}`.toLowerCase();
  }

  return `${number}-${unit}-${direction}-${date.getDate()}-${monthName}-${year}`.toLowerCase();
}

function createStatementFromQuery(query: Query, calculatedDate: Date, locale: string, t: TFunction): string {
  let statement = "";

  if (locale === "ru") {
    statement += query.direction === "from"
      ? "Через "
      : "";
  }

  statement += query.number.toLocaleString(locale) + " ";

  switch (query.unit) {
    case "days":
      statement += t("calendar.days", { count: query.number });
      break;
    case "workingdays":
      statement += t("calendar.workingdays", { count: query.number });
      break;
    case "weeks":
      statement += t("calendar.weeks_dative", { count: query.number });
      break;
    case "months":
      statement += t("calendar.months", { count: query.number });
      break;
    case "years":
      statement += t("calendar.years", { count: query.number });
      break;
  }

  statement += " ";

  switch (query.direction) {
    case "before":
      statement += t("before");
      break;
    case "from":
      statement += t("from");
      break;
  }

  statement += " ";

  if (query.dynamicDate !== null) {
    switch (query.dynamicDate) {
      case "today":
        statement += t("calendar.today");
        break;
      case "tomorrow":
        statement += t("calendar.tomorrow");
        break;
    }
  } else {
    statement += formatLongDate(query.date, locale);
  }

  if (query.direction === "before") {
    statement += " " + t(`${TKEY}.was`);
  } else {
    statement += " " + t(`${TKEY}.will_be`);
  }

  return statement + ":";
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";

  let query: Query | null = null;

  try {
    query = getQueryFromPath((new URL(request.url)).pathname);
  } catch (e) {
    if (e instanceof EdgeCaseRedirectError) {
      return redirect(`/${locale}/${CATEGORY}/count/${e.newSlug}`, 301);
    }
    if (e instanceof InvalidQueryError) {
      throw new Response(null, {
        status: 404,
        statusText: "Not Found"
      });
    }
  }

  const t = await getTranslator(request);


  // Calculate result if query is set
  let error: string | null = null;
  let calculatedDate: Date | null = null;
  try {
    calculatedDate = query === null
      ? null
      : calculateResultingDate(query.number, query.unit, query.direction, query.date)
    ;
  } catch (e) {
    // Handle errors of different types
    if (e instanceof InvalidDateError) {
      error = t(`${TKEY}.error_invalid_date`);
    } else {
      error = t("error_unknown");
    }
  }

  const title = query ? createTitleFromQuery(query, locale, t, TKEY) : t(`${TKEY}.title`);
  let info = null;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];


  if (query !== null && calculatedDate !== null) {
    const slug = createSlug(query.number, query.unit, query.direction, query.date);
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${slug}` });

    const dateStr = calculatedDate.toISOString().split("T")[0];

    const datesDiff: DateDiff = getDatesDiff(calculatedDate, query.date);

    info = {
      isWorkingDay: locale === "ru"
        ? !workSchedule.holidays.includes(dateStr) && !workSchedule.nowork.includes(dateStr)
        : null,
      dayOfWeek: calculatedDate.toLocaleDateString(locale, { weekday: "long" }),
      ...dateInfo(calculatedDate),
      zodiacSign: getZodiacSign(calculatedDate.getDate(), calculatedDate.getMonth() + 1),
      chineseZodiacSign: getChineseZodiac(calculatedDate.getFullYear()),
      periodInSeconds: datesDiff.totalMinutes * 60,
      periodInMinutes: datesDiff.totalMinutes,
      periodInHours: datesDiff.totalHours,
      periodInDays: datesDiff.totalDays,
      periodInWeeks: Math.floor(datesDiff.totalDays / 7),
      periodInMonths: datesDiff.totalMonths,
      periodInYears: datesDiff.totalYears
    };
  }

  const generateTitleForDay = function(day: number): string {
    const commonDaysQuery: Query = {
      number: day,
      unit: "days",
      direction: "from",
      date: new Date(),
      dynamicDate: "today"
    };
    return createTitleFromQuery(commonDaysQuery, locale, t, TKEY);
  };

  let shouldIndex: ShouldIndexMeta = {
    googlebot: query === null,
    yandex: false,
    bingbot: false,
  };

  if (query === null) {
    shouldIndex.yandex = true;
    shouldIndex.bingbot = true;
  } else if (query.dynamicDate !== null) {
    let yandexIndex = false;
    if (query.unit === "days" || query.unit === "workingdays") {
      yandexIndex = query.number <= 365
        || (query.number >= 400 && query.number <= 1000 && query.number % 100 === 0)
        || ([10000, 100000, 1000000, 10000000].includes(query.number));
    } else if (query.unit === "weeks") {
      yandexIndex = query.number <= 100 || ([400, 500, 600, 700, 800, 900, 1000, 10000, 100000, 1000000].includes(query.number));
    } else if (query.unit === "months") {
      yandexIndex = query.number <= 100;
    } else if (query.unit === "years") {
      yandexIndex = query.number <= 100 || ([1000, 10000, 100000].includes(query.number));
    }
    shouldIndex.yandex = yandexIndex;
    shouldIndex.bingbot = yandexIndex;
  } else if (query.date && query.date.getFullYear() === new Date().getFullYear()) {
    shouldIndex.yandex = true;
    shouldIndex.bingbot = true;
  }

  return {
    query: query === null ? null : {
      number: query.number,
      formattedNumber: query.number.toLocaleString(locale),
      unit: query.unit,
      direction: query.direction,
      date: query.date,
      dynamicDate: query.dynamicDate
    },
    statement: query !== null && calculatedDate != null
      ? createStatementFromQuery(query, calculatedDate, locale, t)
      : null,
    calculatedDate: calculatedDate?.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    }),
    todayIs: formatLongDate(new Date(), locale),
    periodInfo: calculatedDate?.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    }),
    otherLinks: [
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/100-days-from-today`, title: generateTitleForDay(100) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/60-days-from-today`, title: generateTitleForDay(60) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/90-days-from-today`, title: generateTitleForDay(90) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/80-days-from-today`, title: generateTitleForDay(80) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/62-days-from-today`, title: generateTitleForDay(62) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/56-days-from-today`, title: generateTitleForDay(56) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/45-days-from-today`, title: generateTitleForDay(45) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/40-days-from-today`, title: generateTitleForDay(40) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/36-days-from-today`, title: generateTitleForDay(36) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/28-days-from-today`, title: generateTitleForDay(28) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/21-days-from-today`, title: generateTitleForDay(21) },
      { to: `/${locale}/${CATEGORY}/${ROUTE_ENDING}/7-days-from-today`, title: generateTitleForDay(7) }
    ],
    info,
    error,
    meta: {
      locale,
      title: query
        ? createTitleFromQuery(query, locale, t, TKEY) + " — " + t(`${TKEY}.title_short`)
        : t(`${TKEY}.title`),
      // Exclude paeges which have specific dates from which to count, and which are not current year
      shouldIndex,
      h1: query
        ? createTitleFromQuery(query, locale, t, TKEY)
        : t(`${TKEY}.title`),
      description: query ? createDescriptionFromQuery(query, locale, t, TKEY) : t(`${TKEY}.description`),
      breadcrumbs,
      canonical: getBaseUrl() + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING,
      currentUrl: request.url,
      images: query === null ? [] : [
        `/${locale}/dates/count-img/${createSlug(query.number, query.unit, query.direction, query.date)}.svg`
      ],
      lastModified: query?.dynamicDate
        ? (new Date()).toISOString()
        : LAST_UPDATED.toISOString()
    }
  };
};

function calculate(e: FormEvent<HTMLFormElement>) {
  e.preventDefault();

  const formData = new FormData(e.currentTarget);

  // Use formData to get your values, e.g., number, unit, direction, date
  const number = parseInt(formData.get("number") as string, 10);
  const unit = formData.get("unit") as string;
  const direction = formData.get("direction") as string;
  const date = new Date(formData.get("date") as string);


  // Assume you have a "createSlug" function that accepts `resultingDate` as parameter
  const slug = createSlug(number, unit, direction, date);

  // Analyze the current URL and replace or add the slug accordingly
  let currentPathname = window.location.pathname;
  const slugPattern = new RegExp(`/${ROUTE_ENDING}/?(.*)$`);

  if (slugPattern.test(currentPathname)) {
    // Replace existing slug
    currentPathname = currentPathname.replace(slugPattern, `/${ROUTE_ENDING}/${slug}`);
  } else {
    // Add slug to the pathname
    currentPathname = currentPathname.endsWith("/") ? `/${ROUTE_ENDING}/${slug}` : `/${ROUTE_ENDING}/${slug}/`;
  }

  // Redirect to the new path if it changed
  const newUrl = window.location.origin + currentPathname;
  if (window.location.href === newUrl) {
    return;
  }

  window.location.href = newUrl;
}

function getQueryFromPath(path: string): Query | null {
  const lastPathPart = path.split("/").pop();
  const slug = (!lastPathPart || lastPathPart === ROUTE_ENDING)
    ? null
    : lastPathPart
  ;

  // Parse a query from the slug
  const query = slug === null ? null : parseSlug(slug);
  console.debug("Parsed query", query);

  return query;
}

export default function Index() {
  const {
    meta,
    query,
    statement,
    calculatedDate,
    todayIs,
    otherLinks,
    info,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const { t: tDates } = useTranslation("dates");

  const locale = getLocale(useLocation().pathname) ?? "";

  return (<>
    <CalculatorLayout>
      <ToolHeaderNew
        name={meta.h1}
        description={meta.description}
      />
      {calculatedDate && !error ? <Result color="positive">
        <p>{t(`${TKEY}.today_is`, { date: todayIs })}.</p>
        <p>{statement}</p>

        <p className="my-6 text-3xl font-bold first-letter:capitalize">
          {calculatedDate}
        </p>

        {info === null ? null : <>
          {info?.isWorkingDay !== null ? (
            <p className="mt-2">
              {info.isWorkingDay
                ? "Это рабочий день."
                : "Это не рабочий день."
              }
            </p>
          ) : null}

          <p>
            {
              t(`${TKEY}.day_of_season`, { count: info.dayOfSeason, season: t(`${TKEY}.of_${info.season}`) })
              + ", " +
              t(`${TKEY}.day_of_year`, { count: info.dayOfYear }) + ". "
            }
            <br />
            {
              t(`${TKEY}.leap_year_${info.isLeapYear ? "yes" : "no"}`) + ". " +
              t(`${TKEY}.days_till_end_of_year`, { count: info.daysTillEndOfYear }) + ". "
            }
          </p>

          {info.zodiacSign === null ? (
            <p dangerouslySetInnerHTML={{
              __html: t(`${TKEY}.chinese_zodiac`, { sign: tDates(`chinese_zodiac.${info.chineseZodiacSign}`) })
            }} />
          ) : (
            <p dangerouslySetInnerHTML={{
              __html: t(`${TKEY}.both_zodiacs`, {
                chinese: tDates(`chinese_zodiac.${info.chineseZodiacSign}`),
                western: tDates(`zodiac.${info.zodiacSign}`)
              })
            }} />
          )}
        </>}

        {/* TODO Click to copy */}
        <p>{t(`${TKEY}.as_unix_timestamp`, {
          date: calculatedDate,
          timestamp: info?.unixTimestamp ?? 'N/A'
        }).replace(/^./, str => str.toUpperCase())}</p>
      </Result> : null}
      <ToolForm onSubmit={calculate}>
        <span className="font-semibold">{t(`${TKEY}.which_date`)}</span>
        <div className="w-full md:w-[100px]">
          <BetterNumberInput
            name="number"
            defaultValue={query?.number ?? 30}
            min={1}
            maxFractionDigits={0}
            ariaLabel={t(`${TKEY}.input_number`)}
            autoFocus={true}
          />
        </div>

        <div className="w-full md:w-[160px]">
          <SelectInput
            name="unit"
            ariaLabel={t(`${TKEY}.input_unit`)}
            defaultValue={query?.unit ?? "days"}
            options={
              [
                { value: "days", name: t("calendar.days_other") },
                ...(meta.locale === "ru" ? [{
                  value: "workingdays",
                  name: t("calendar.workingdays_other")
                }] : []),
                { value: "weeks", name: t("calendar.weeks_other") },
                { value: "months", name: t("calendar.months_other") },
                { value: "years", name: t("calendar.years_other") }
              ]
            }
          />
        </div>

        <div className="w-full md:w-[150px]">
          <SelectInput
            name="direction"
            defaultValue={query?.direction ?? "from"}
            options={
              [
                { value: "from", name: t("from") },
                { value: "before", name: t("before") }
              ]
            }
            ariaLabel={t(`${TKEY}.input_direction`)}
          />
        </div>

        <div className="w-full xl:w-auto ">
          <DatePicker
            name="date"
            value={query?.date ? new Date(query.date) : new Date()}
            ariaLabel={t(`${TKEY}.input_date`)}
          />
        </div>

        <Button className="w-full" isSubmit={true}>
          <Icon v="keypad" className="pr-2 pt-1 pb-1" /> {t("do_calculate")}
        </Button>
      </ToolForm>

      {error ?
        <Result color="negative">
          <p>{error}</p>
        </Result>
        : null}
    </CalculatorLayout>

    <AdditionalContentLayout>

      {error ? null : (
        <AdditionalInfo>
          {(query !== null && info !== null && query.unit !== "workingdays") ? (
            <AdditionalInfoSection
              id="other-units"
              contentsText={t(`${TKEY}.info_other_units_h_days`, {
                count: query.number,
                number: query.formattedNumber
              })}
              contentsEmoji="📏"
            >
              {query.unit === "days" ?
                <H2 className="mb-3">{t(`${TKEY}.info_other_units_h_days`, {
                  count: query.number,
                  number: query.formattedNumber
                })}</H2>
                : null}
              {query.unit === "weeks" ?
                <H2 className="mb-3">{t(`${TKEY}.info_other_units_h_weeks`, {
                  count: query.number,
                  number: query.formattedNumber
                })}</H2>
                : null}
              {query.unit === "months" ?
                <H2 className="mb-3">{t(`${TKEY}.info_other_units_h_months`, {
                  count: query.number,
                  number: query.formattedNumber
                })}</H2>
                : null}
              {query.unit === "years" ?
                <H2 className="mb-3">{t(`${TKEY}.info_other_units_h_years`, {
                  count: query.number,
                  number: query.formattedNumber
                })}</H2>
                : null}
              <ul className="list-ol">
                <li>{info.periodInSeconds.toLocaleString(locale)} {t(`calendar.seconds`, { count: info.periodInSeconds })}</li>
                {info.periodInMinutes === 0 ? null : (
                  <li>{info.periodInMinutes.toLocaleString(locale)} {t(`calendar.minutes`, { count: info.periodInMinutes })}</li>
                )}
                {info.periodInHours === 0 ? null : (
                  <li>{info.periodInHours.toLocaleString(locale)} {t(`calendar.hours`, { count: info.periodInHours })}</li>
                )}
                {query.unit === "days" || info.periodInDays === 0 ? null : (
                  <li>{info.periodInDays.toLocaleString(locale)} {t(`calendar.days`, { count: info.periodInDays })}</li>
                )}
                {query.unit === "weeks" || info.periodInWeeks === 0 ? null : (
                  <li>{info.periodInWeeks.toLocaleString(locale)} {t(`calendar.weeks`, { count: info.periodInWeeks })}</li>
                )}
                {query.unit === "months" || info.periodInMonths === 0 ? null : (
                  <li>{info.periodInMonths.toLocaleString(locale)} {t(`calendar.months`, { count: info.periodInMonths })}</li>
                )}
                {query.unit === "years" || info.periodInYears === 0 ? null : (
                  <li>{info.periodInYears.toLocaleString(locale)} {t(`calendar.years`, { count: info.periodInYears })}</li>
                )}
              </ul>
            </AdditionalInfoSection>
          ) : null}

          {query === null ? (
            <AdditionalInfoSection
              id="excel-guides"
              contentsText={t(`${TKEY}.excel_general_h`)}
              contentsEmoji="📊"
            >
              <H2 anchor="excel" className="mb-3">{t(`${TKEY}.excel_general_h`)}</H2>
              <p>
                <Trans
                  t={t}
                  i18nKey={`${TKEY}.excel_general_intro`}
                  lang={locale}
                  components={{ Code: <Code /> }}
                />
              </p>

              <H3 className="mt-3">{t(`${TKEY}.excel_general_days_h`)}</H3>
              <p>{t(`${TKEY}.excel_general_days_p`)}</p>
              <div>
                <table className="table-auto w-auto">
                  <tbody>
                  <tr>
                    <td className="pr-2 py-1">{t(`${TKEY}.excel_general_days_add_days`)}</td>
                    <td className="pl-2 py-1"><Code>{t(`${TKEY}.excel_general_days_add_days_formula`)}</Code></td>
                  </tr>
                  <tr>
                    <td className="pr-2 py-1">{t(`${TKEY}.excel_general_days_subtract_days`)}</td>
                    <td className="pl-2 py-1"><Code>{t(`${TKEY}.excel_general_days_subtract_days_formula`)}</Code>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <H3 className="mt-3">{t(`${TKEY}.excel_general_weeks_h`)}</H3>
              <p>{t(`${TKEY}.excel_general_weeks_p`)}</p>

              <div>
                <table className="table-auto w-auto">
                  <tbody>
                  <tr>
                    <td className="pr-2 py-1">{t(`${TKEY}.excel_general_weeks_add_weeks`)}</td>
                    <td className="pl-2 py-1"><Code>{t(`${TKEY}.excel_general_weeks_add_weeks_formula`)}</Code></td>
                  </tr>
                  <tr>
                    <td className="pr-2 py-1">{t(`${TKEY}.excel_general_weeks_subtract_weeks`)}</td>
                    <td className="pl-2 py-1"><Code>{t(`${TKEY}.excel_general_weeks_subtract_weeks_formula`)}</Code>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <H3 className="mt-3">{t(`${TKEY}.excel_general_months_h`)}</H3>
              <p dangerouslySetInnerHTML={{
                __html: t(`${TKEY}.excel_general_months_p`)
              }} />
              <div>
                <table className="table-auto w-auto">
                  <tbody>
                  <tr>
                    <td className="pr-2 py-1">{t(`${TKEY}.excel_general_add_months`)}</td>
                    <td className="pl-2 py-1"><Code>{t(`${TKEY}.excel_general_add_months_formula`)}</Code></td>
                  </tr>
                  <tr>
                    <td className="pr-2 py-1">{t(`${TKEY}.excel_general_subtract_months`)}</td>
                    <td className="pl-2 py-1"><Code>{t(`${TKEY}.excel_general_subtract_months_formula`)}</Code></td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <H3 className="mt-3">{t(`${TKEY}.excel_general_years_h`)}</H3>
              <p>
                <Trans
                  t={t}
                  i18nKey={`${TKEY}.excel_general_years_p`}
                  lang={locale}
                  components={{ Code: <Code />, a: <a /> }}
                />
              </p>

              <p>{t(`${TKEY}.excel_general_years_p_both`)}</p>

              <div className="w-full overflow-x-auto">
                <table className="table-auto w-auto">
                  <thead>
                  <tr>
                    <th
                      className="pr-5 py-3 border-b border-gray-200 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      {t(`${TKEY}.excel_general_years_t_formula`)}
                    </th>
                    <th
                      className="px-5 py-3 border-b border-gray-200 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      {t(`${TKEY}.excel_general_years_t_comment`)}
                    </th>
                    <th
                      className="px-5 py-3 border-b border-gray-200 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      {t(`${TKEY}.excel_general_years_t_example_date`)}
                    </th>
                    <th
                      className="pl-5 py-3 border-b border-gray-200 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider">
                      {t(`${TKEY}.excel_general_years_t_example_result`)}
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td className="pr-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t1_formula`)}
                    </td>
                    <td className="px-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t1_comment`)}
                    </td>
                    <td className="px-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t1_example_date`)}
                    </td>
                    <td className="pl-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t1_example_result`)}
                    </td>
                  </tr>
                  <tr>
                    <td className="pr-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t2_formula`)}
                    </td>
                    <td className="px-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t2_comment`)}
                    </td>
                    <td className="px-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t2_example_date`)}
                    </td>
                    <td className="pl-5 py-5 border-b border-gray-300 text-sm">
                      {t(`${TKEY}.excel_general_years_t2_example_result`)}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <p>{t(`${TKEY}.excel_general_years_p_date_def`)}</p>
              <p>{t(`${TKEY}.excel_general_years_p_edate_def`)}</p>

              <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.excel_general_years_p_more`) }} />

              <p>{t(`${TKEY}.excel_sources`)}:</p>
              <ul className="list-ol">
                <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.excel_sources_1`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.excel_sources_2`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${TKEY}.excel_sources_3`) }} />
              </ul>
            </AdditionalInfoSection>
          ) : (
            <>
              {query.unit === "days" ? (
                <AdditionalInfoSection
                  id="days"
                  contentsText={query.direction === "from"
                    ? t(`${TKEY}.excel_days_h_add`, { count: query.number, number: query.formattedNumber })
                    : t(`${TKEY}.excel_days_h_subtract`, { count: query.number, number: query.formattedNumber })
                  }
                  contentsEmoji="🗓️">
                  <H2 className="mb-3">
                    {query.direction === "from"
                      ? t(`${TKEY}.excel_days_h_add`, { count: query.number, number: query.formattedNumber })
                      : t(`${TKEY}.excel_days_h_subtract`, { count: query.number, number: query.formattedNumber })
                    }
                  </H2>

                  <p>
                    <Trans
                      t={t}
                      i18nKey={`${TKEY}.excel_days_p1`}
                      lang={locale}
                      components={{ Code: <Code />, a: <a /> }}
                      count={query.number}
                    />
                  </p>

                  <CodeBlock>
                    ={t(`${TKEY}.excel_formula_today`)}() {query.direction === "from" ? "+" : "-"} {query.number}
                  </CodeBlock>

                  <p>{t(`${TKEY}.excel_replace_today`)}</p>
                  <p>
                    <Trans
                      t={t}
                      i18nKey={`${TKEY}.excel_learn_more`}
                      lang={locale}
                      components={{ Link: <Link to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}#excel`} />, a: <a /> }}
                    />
                  </p>
                </AdditionalInfoSection>
              ) : null}

              {query.unit === "weeks" ? (
                <AdditionalInfoSection
                  id="weeks"
                  contentsText={query.direction === "from"
                    ? t(`${TKEY}.excel_weeks_h_add`, { count: query.number, number: query.formattedNumber })
                    : t(`${TKEY}.excel_weeks_h_subtract`, { count: query.number, number: query.formattedNumber })
                  }
                  contentsEmoji="📅">
                  <H2 className="mb-3">
                    {query.direction === "from"
                      ? t(`${TKEY}.excel_weeks_h_add`, { count: query.number, number: query.formattedNumber })
                      : t(`${TKEY}.excel_weeks_h_subtract`, { count: query.number, number: query.formattedNumber })
                    }
                  </H2>

                  <p>{t(`${TKEY}.excel_weeks_p`)}</p>

                  <CodeBlock>
                    ={t(`${TKEY}.excel_formula_today`)}() {query.direction === "from" ? "+" : "-"} ({query.number} *
                    7)
                  </CodeBlock>

                  <p>{t(`${TKEY}.excel_replace_today`)}</p>
                  <p>
                    <Trans
                      t={t}
                      i18nKey={`${TKEY}.excel_learn_more`}
                      lang={locale}
                      components={{ Link: <Link to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}#excel`} />, a: <a /> }}
                    />
                  </p>
                </AdditionalInfoSection>
              ) : null}

              {query.unit === "months" ? (
                <AdditionalInfoSection
                  id="months"
                  contentsText={query.direction === "from"
                    ? t(`${TKEY}.excel_months_h_add`, { count: query.number, number: query.formattedNumber })
                    : t(`${TKEY}.excel_months_h_subtract`, { count: query.number, number: query.formattedNumber })
                  }
                  contentsEmoji="🗓️">
                  <H2 className="mb-3">
                    {query.direction === "from"
                      ? t(`${TKEY}.excel_months_h_add`, { count: query.number, number: query.formattedNumber })
                      : t(`${TKEY}.excel_months_h_subtract`, { count: query.number, number: query.formattedNumber })
                    }
                  </H2>

                  <p>
                    <Trans
                      t={t}
                      i18nKey={`${TKEY}.excel_months_p`}
                      lang={locale}
                      components={{ Code: <Code /> }}
                      count={query.number}
                    />
                  </p>

                  <CodeBlock>
                    ={t(`${TKEY}.excel_formula_edate`)}({t(`${TKEY}.excel_formula_today`)}(){t(`${TKEY}.excel_formula_separator`)} {query.direction === "from" ? "+" : "-"}{query.number})
                  </CodeBlock>

                  <p>{t(`${TKEY}.excel_replace_today`)}</p>
                  <p>
                    <Trans
                      t={t}
                      i18nKey={`${TKEY}.excel_learn_more`}
                      lang={locale}
                      components={{ Link: <Link to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}#excel`} />, a: <a /> }}
                    />
                  </p>
                </AdditionalInfoSection>
              ) : null}

              {query.unit === "years" ? (
                <AdditionalInfoSection
                  id="years"
                  contentsText={query.direction === "from"
                    ? t(`${TKEY}.excel_years_h_add`, { count: query.number, number: query.formattedNumber })
                    : t(`${TKEY}.excel_years_h_subtract`, { count: query.number, number: query.formattedNumber })
                  }
                  contentsEmoji="📈">
                  <H2 className="mb-3">
                    {query.direction === "from"
                      ? t(`${TKEY}.excel_years_h_add`, { count: query.number, number: query.formattedNumber })
                      : t(`${TKEY}.excel_years_h_subtract`, { count: query.number, number: query.formattedNumber })
                    }
                  </H2>

                  <p dangerouslySetInnerHTML={{ __html: t(`${TKEY}.excel_years_p`) }} />

                  <CodeBlock>
                    {t(`${TKEY}.excel_formula_year_with_date`, { count: query.number })}
                  </CodeBlock>

                  <p>{t(`${TKEY}.excel_years_formula_1_comment`)}</p>
                  <p>{t(`${TKEY}.excel_years_formula_2_intro`)}</p>

                  <CodeBlock>
                    ={t(`${TKEY}.excel_formula_edate`)}({t(`${TKEY}.excel_formula_today`)}(){t(`${TKEY}.excel_formula_separator`)} {query.direction === "from" ? `${query.number} * 12` : `-(${query.number} * 12)`})
                  </CodeBlock>

                  <p>{t(`${TKEY}.excel_years_formula_2_comment`)}</p>
                  <p>{t(`${TKEY}.excel_replace_today`)}</p>
                  <p>
                    <Trans
                      t={t}
                      i18nKey={`${TKEY}.excel_years_learn_more`}
                      lang={locale}
                      components={{ Link: <Link to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}#excel`} />, a: <a /> }}
                    />
                  </p>
                </AdditionalInfoSection>
              ) : null}
            </>
          )}

          {query === null ? (
            <AdditionalInfoSection
              id="info-about"
              contentsText={t(`${TKEY}.info_about_h`)}
              contentsEmoji="ℹ️"
            >
              <H2 className="mb-3">{t(`${TKEY}.info_about_h`)}</H2>
              <p>{t(`${TKEY}.info_about_p1`)}</p>
              <p>{t(`${TKEY}.info_about_p2`)}</p>
              <p>{t(`${TKEY}.info_about_p3`)}</p>
            </AdditionalInfoSection>
          ) : null}

          {meta.images.length > 0 ? (
            <ArticleImage
              src={meta.images[0]}
              alt={meta.title}
              caption={meta.title} // TODO Should be different
              width={1920}
              height={1080}
            />
          ) : null}

          <AdditionalInfoSection
            id="similar-pages"
            contentsText={t(`${TKEY}.similar_pages`)}
            contentsEmoji="🔗"
          >
            <H2 className="mb-3">{t(`${TKEY}.similar_pages`)}</H2>
            <ul>
              {otherLinks.map((link, i) => (
                <li key={i}><Link to={link.to} rel="nofollow">{link.title}</Link></li>
              ))}
            </ul>
          </AdditionalInfoSection>
        </AdditionalInfo>
      )}
      <Author lastUpdated={LAST_UPDATED} />
    </AdditionalContentLayout>
  </>);
}

